import { ITeamDetails, TeamAbrvID, TeamAbrvType } from '@shared/types';
import { saveTeamDetails } from 'api/TeamsAPI';
import { useAppDispatch } from 'app/hooks';
import { updateTeam } from 'app/slices/LeagueSlice';
import { TeamRivalries } from 'constants/rivalries-constants';
import { areArraysEqual } from 'helpers/ArrayHelpers';
import { TeamAbrvToTeamID, toTeamAbrvID } from 'helpers/IdHelpers';
import { TeamColor, TeamSecondaryColor } from 'helpers/TeamHelpers';
import useSelectedTeamInfo from 'hooks/useSelectedTeamInfo';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldTurfType, NFLConference, NFLDivision } from 'types';

export type UseEditTeam = ReturnType<typeof useEditTeam>;
const useEditTeam = () => {
  const dispatch = useAppDispatch();
  const { teamStandings, teamDetails, weeklyTeamStandings, schedule } = useSelectedTeamInfo();

  const [editMode, setEditMode] = useState<boolean>(false);

  const [selectedConference, setSelectedConference] = useState<NFLConference>();
  const [selectedDivision, setSelectedDivision] = useState<NFLDivision>();
  const [teamName, setTeamName] = useState<string>('');
  const [teamFullName, setTeamFullName] = useState<string>('');
  const [teamCity, setTeamCity] = useState<string>('');
  const [teamState, setTeamState] = useState<string>('');
  const [teamStadium, setTeamStadium] = useState<string>('');
  const [stadiumCapacity, setStadiumCapacity] = useState<number>(0);
  const [teamStadiumHasRoof, setTeamStadiumHasRoof] = useState<boolean>(false);
  const [teamStadiumFieldType, setTeamStadiumFieldType] = useState<FieldTurfType>('ArtificialTurf');
  const [teamRivalries, setTeamRivalries] = useState<TeamAbrvID[]>([]);
  const [disabledTeams, setDisabledTeams] = useState<TeamAbrvID[]>([]);

  useEffect(() => {
    if (teamDetails) {
      setSelectedConference(teamDetails.teamConference);
      setSelectedDivision(teamDetails.teamDivision);
      setTeamName(teamDetails.teamName);
      setTeamFullName(teamDetails.teamFullName);
      setTeamCity(teamDetails.teamCity);
      setTeamState(teamDetails.teamState);
      setTeamStadium(teamDetails.teamStadium);
      setStadiumCapacity(teamDetails.teamCapacity);
      setTeamStadiumHasRoof(!!teamDetails.teamStadiumHasRoof);
      setTeamStadiumFieldType(teamDetails.teamStadiumFieldType ?? 'ArtificialTurf');

      const teamAbrvID = toTeamAbrvID(teamDetails.teamID);
      const rivalries = TeamRivalries[teamAbrvID];
      setTeamRivalries(teamDetails.rivalries ?? rivalries);
      setDisabledTeams([teamAbrvID]);
    }
  }, [teamDetails]);

  const isDirty = useMemo(() => {
    if (!teamDetails || !selectedConference || !selectedDivision) {
      return false;
    }

    return (
      teamDetails.teamConference !== selectedConference ||
      teamDetails.teamDivision !== selectedDivision ||
      teamDetails.teamName !== teamName ||
      teamDetails.teamFullName !== teamFullName ||
      teamDetails.teamCity !== teamCity ||
      teamDetails.teamState !== teamState ||
      teamDetails.teamStadium !== teamStadium ||
      teamDetails.teamCapacity !== stadiumCapacity ||
      teamDetails.teamStadiumFieldType !== teamStadiumFieldType ||
      areArraysEqual(teamDetails.rivalries ?? [], teamRivalries) === false ||
      !!teamDetails.teamStadiumHasRoof !== teamStadiumHasRoof
    );
  }, [
    teamDetails,
    selectedConference,
    selectedDivision,
    teamName,
    teamFullName,
    teamCity,
    teamState,
    teamStadium,
    stadiumCapacity,
    teamStadiumFieldType,
    teamStadiumHasRoof,
    teamRivalries,
  ]);

  const onUpdateTeamInfo = useCallback(async () => {
    if (teamDetails && selectedConference && selectedDivision) {
      const updatedTeamDetails: ITeamDetails = {
        ...teamDetails,
        teamConference: selectedConference,
        teamDivision: selectedDivision,
        ...(teamName ? { teamName: teamName.trim() } : {}),
        ...(teamFullName ? { teamFullName: teamFullName.trim() } : {}),
        ...(teamCity ? { teamCity: teamCity.trim() } : {}),
        ...(teamState ? { teamState } : {}),
        ...(teamStadium ? { teamStadium: teamStadium.trim() } : {}),
        ...(stadiumCapacity ? { teamCapacity: stadiumCapacity } : {}),
        ...(teamStadiumFieldType ? { teamStadiumFieldType: teamStadiumFieldType } : {}),
        ...(typeof teamStadiumHasRoof !== 'undefined' ? { teamStadiumHasRoof: teamStadiumHasRoof } : {}),
        teamColor: TeamColor[TeamAbrvToTeamID[teamDetails.teamAbrv as TeamAbrvType]],
        teamSecondaryColor: TeamSecondaryColor[TeamAbrvToTeamID[teamDetails.teamAbrv as TeamAbrvType]],
        rivalries: teamRivalries,
      };

      await saveTeamDetails(updatedTeamDetails);
      dispatch(updateTeam(updatedTeamDetails));

      setTimeout(() => {
        setEditMode(false);
      }, 1000);
    }
  }, [
    teamDetails,
    selectedConference,
    selectedDivision,
    teamName,
    teamFullName,
    teamCity,
    teamState,
    teamStadium,
    stadiumCapacity,
    teamStadiumHasRoof,
    teamStadiumFieldType,
    teamRivalries,
    dispatch,
  ]);

  return {
    teamStandings,
    teamDetails,
    weeklyTeamStandings,
    schedule,
    editMode,
    setEditMode,
    selectedConference,
    selectedDivision,
    teamName,
    teamFullName,
    teamCity,
    teamState,
    teamStadium,
    stadiumCapacity,
    teamStadiumHasRoof,
    teamStadiumFieldType,
    teamRivalries,
    disabledTeams,
    setSelectedConference,
    setSelectedDivision,
    setTeamName,
    setTeamFullName,
    setTeamCity,
    setTeamState,
    setTeamStadium,
    setStadiumCapacity,
    setTeamStadiumHasRoof,
    setTeamStadiumFieldType,
    setTeamRivalries,
    isDirty: isDirty,
    onUpdateTeamInfo,
  };
};

export default useEditTeam;
